import { observer } from 'mobx-react-lite';
import { Fragment, useMemo, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { Grid, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Page } from '@/design-system-v2/page';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { JustConnectedSuggestions } from '@/features/home/IntegrationSuggestionsSection';
import {
  GridItemCard,
  gridItemKey,
} from '@/features/integrations/add-integrations-modal';
import {
  reconnectAccounts,
  useIntegrationGrid,
  type GridItem,
  type Selection,
  type StatusFilter,
} from '@/features/integrations/add-integrations.hooks';
import {
  ON_DEVICE_SELECTION,
  type IntegrationSuggestion,
} from '@/features/integrations/integrations.types';
import { useConnectFromLink } from '@/features/integrations/use-connect-from-link';
import { YourDevicesSection } from '@/features/integrations/your-devices-section';
import {
  INTEGRATION_MANAGEMENT_HASH,
  integrationManagementSearchId,
} from '@/features/settings/settings-search-dom';
import { useHashRoute } from '@/hooks/useHashRoute';
import { hasIntegrationAccess } from '@/integrations/integrations.utils';
import {
  useIntegrationActions,
  type HandleIntegrationAction,
} from '@/integrations/useIntegrationActions';
import { trackEvent } from '@/utils/analytics';

import { InstalledIntegrations } from './installed-integrations';
import { ReconnectAccounts } from './reconnect-accounts';

import type { ProviderKey } from '@littlebird/integrations';

const noop = () => {};
const INSTALLED_SELECTION = 'installed';
const VISIBLE_SECTION_COUNT = 7;

const IntegrationCards = ({
  items,
  handleAction,
  openSettings,
  requiresUpgrade,
  openUpgrade,
  dismissSuggestion,
}: {
  items: GridItem[];
  handleAction: HandleIntegrationAction;
  openSettings: (providerKey?: ProviderKey) => void;
  requiresUpgrade: boolean;
  openUpgrade: (source: string) => void;
  dismissSuggestion: (suggestion: IntegrationSuggestion) => void;
}) => {
  return (
    // -mx-2 cancels the cards' p-2, so their icons line up with the label
    <Grid className="-mx-2 grid-cols-2 gap-5">
      {items.map((item) => (
        <GridItemCard
          key={gridItemKey(item)}
          item={item}
          handleAction={handleAction}
          openSettings={openSettings}
          requiresUpgrade={requiresUpgrade}
          openUpgrade={openUpgrade}
          dismiss={noop}
          dismissSuggestion={dismissSuggestion}
        />
      ))}
    </Grid>
  );
};

export const PluginsIntegrations = observer(
  ({
    query,
    filter,
    toolbar,
  }: {
    query: string;
    filter: StatusFilter;
    toolbar?: ReactNode;
  }) => {
    const [selection, setSelection] = useState<Selection>('all');
    const { handleAction } = useIntegrationActions('integrations_modal');
    const { setHash } = useHashRoute();
    const hasFeature = useFeature();
    const requiresUpgrade =
      !hasIntegrationAccess() && !hasFeature(FEATURES.WEB_ONBOARDING_FEED);
    const openSettings = (providerKey?: ProviderKey) =>
      setHash(
        providerKey
          ? `settings/Integrations/${integrationManagementSearchId(providerKey)}`
          : INTEGRATION_MANAGEMENT_HASH
      );
    const openUpgrade = (source: string) => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source });
      setHash('settings/Subscription');
    };
    const {
      isLoading,
      loadError,
      retryLoad,
      allItems,
      visibleItems,
      sections,
      effectiveSelection,
      dismissSuggestion,
    } = useIntegrationGrid({
      search: query,
      filter,
      selection: selection === INSTALLED_SELECTION ? 'all' : selection,
    });
    useConnectFromLink(allItems, isLoading);
    const normalizedQuery = query.trim().toLowerCase();
    const hasInstalledIntegrations = sections.some((section) =>
      section.items.some((item) => item.isConnected)
    );
    const installedSelected =
      selection === INSTALLED_SELECTION && hasInstalledIntegrations;
    const installedItems = visibleItems.filter((item) => item.isConnected);
    const selectedSection = installedSelected
      ? INSTALLED_SELECTION
      : effectiveSelection;
    const sectionOptions = [
      { slug: 'all', label: 'All' },
      ...(hasInstalledIntegrations
        ? [{ slug: INSTALLED_SELECTION, label: 'Connected' }]
        : []),
      ...sections.map(({ slug, label }) => ({ slug, label })),
    ];
    const visibleSectionOptions = sectionOptions.slice(
      0,
      VISIBLE_SECTION_COUNT
    );
    const moreSectionOptions = sectionOptions.slice(VISIBLE_SECTION_COUNT);
    const showingAll = !normalizedQuery && selectedSection === 'all';
    const lapsedAccounts = useMemo(
      () => (showingAll ? reconnectAccounts(visibleItems) : []),
      [showingAll, visibleItems]
    );
    const availableSections = useMemo(() => {
      if (installedSelected) {
        const items = visibleItems.filter((item) => item.isConnected);
        return items.length
          ? [{ slug: INSTALLED_SELECTION, label: 'Connected', items }]
          : [];
      }

      if (normalizedQuery) {
        const items = visibleItems;
        return items.length
          ? [{ slug: 'search-results', label: 'Available', items }]
          : [];
      }

      if (effectiveSelection !== 'all') {
        return sections.filter(
          (section) => section.slug === effectiveSelection
        );
      }

      return sections;
    }, [
      effectiveSelection,
      installedSelected,
      normalizedQuery,
      sections,
      visibleItems,
    ]);

    return (
      <YStack className="min-h-0 flex-1">
        <Page.Column width="full" className="shrink-0 gap-3 pt-6">
          {toolbar}
          {!isLoading && !loadError ? (
            <ScrollMask
              direction="horizontal"
              showEndFade
              fadeSize={24}
              className="flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide"
            >
              {visibleSectionOptions.map((section) => {
                const active = selectedSection === section.slug;
                return (
                  <Button
                    key={section.slug}
                    variant={active ? 'default' : 'secondary'}
                    size="sm"
                    aria-pressed={active}
                    onClick={() => setSelection(section.slug)}
                    className="shrink-0 rounded-full px-4"
                  >
                    {section.label}
                  </Button>
                );
              })}
              {moreSectionOptions.length > 0 ? (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      variant={
                        moreSectionOptions.some(
                          (section) => section.slug === selectedSection
                        )
                          ? 'default'
                          : 'secondary'
                      }
                      size="sm"
                      className="shrink-0 rounded-full px-4"
                    >
                      More
                      <Icons.chevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" fit>
                    {moreSectionOptions.map((section) => (
                      <DropdownMenu.Item
                        key={section.slug}
                        onSelect={() => setSelection(section.slug)}
                      >
                        {section.label}
                        {selectedSection === section.slug ? (
                          <Icons.check className="size-3.5" />
                        ) : null}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              ) : null}
            </ScrollMask>
          ) : null}
        </Page.Column>
        <ScrollMask className="min-h-0 flex-1 pt-10 pb-6 scrollbar-hide">
          <Page.Column width="full" className="gap-10 pb-8">
            {isLoading ? (
              <YStack center className="gap-2 py-12">
                <Icons.loader className="size-4 animate-spin text-foreground-muted" />
                <span className="text-xs text-foreground-muted">
                  Loading integrations...
                </span>
              </YStack>
            ) : loadError ? (
              <Empty.Root variant="plain">
                <Empty.Media variant="icon">
                  <Icons.warnTriangle />
                </Empty.Media>
                <Empty.Header>
                  <Empty.Title>Couldn&apos;t load integrations</Empty.Title>
                </Empty.Header>
                <Empty.Actions>
                  <Button size="sm" onClick={retryLoad}>
                    Try again
                  </Button>
                </Empty.Actions>
              </Empty.Root>
            ) : availableSections.length === 0 ? (
              <Empty.Root variant="plain">
                <Empty.Media variant="icon">
                  <Icons.search />
                </Empty.Media>
                <Empty.Header>
                  <Empty.Title>No integrations found</Empty.Title>
                  <Empty.Description>
                    Try adjusting your search.
                  </Empty.Description>
                </Empty.Header>
              </Empty.Root>
            ) : (
              <>
                {showingAll ? <JustConnectedSuggestions /> : null}
                {showingAll && installedItems.length > 0 ? (
                  <InstalledIntegrations
                    items={installedItems}
                    openSettings={openSettings}
                  />
                ) : null}
                {lapsedAccounts.length > 0 ? (
                  <ReconnectAccounts accounts={lapsedAccounts} />
                ) : null}
                {availableSections.map((section) => (
                  <Fragment key={section.slug}>
                    <YStack className="gap-4">
                      <h3 className="text-sm font-medium text-foreground-muted">
                        {section.label}
                      </h3>
                      <IntegrationCards
                        items={section.items}
                        handleAction={handleAction}
                        openSettings={openSettings}
                        requiresUpgrade={requiresUpgrade}
                        openUpgrade={openUpgrade}
                        dismissSuggestion={dismissSuggestion}
                      />
                    </YStack>
                    {selectedSection === ON_DEVICE_SELECTION &&
                    section.slug === ON_DEVICE_SELECTION ? (
                      <YourDevicesSection />
                    ) : null}
                  </Fragment>
                ))}
              </>
            )}
          </Page.Column>
        </ScrollMask>
      </YStack>
    );
  }
);
